import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import AuthService, { User } from '../services/authService';
import SecureTokenStorage from '../utils/tokenStorage';
import { apiFetchJson } from '../utils/apiClient';
import logger from '../utils/logger';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (userData: any) => Promise<void>;
  setUser: (user: User | null) => void;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for stored tokens and fetch user data on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      logger.log('[useAuth] Checking auth status on mount...');
      try {
        // Try to load from cache first for instant display
        const cachedUser = AuthService.getCurrentUserSync();
        if (cachedUser) {
          logger.log('[useAuth] Using cached user for instant display');
          setUser(cachedUser);
        }

        // Then validate token and fetch fresh data in background
        // Only force refresh if we don't have cached data
        const { isValid, user } = await AuthService.ensureValidToken();
        logger.log('[useAuth] Token validation result:', isValid);

        if (isValid) {
          if (user) {
            // User data already fetched during token validation
            logger.log('[useAuth] User data from token validation:', {
              id: user.id,
              email: user.email,
              hasProfileImage: !!user.profileImage,
              profileImage: user.profileImage,
            });
            setUser(user);
          } else {
            // Token is valid but user data not returned, fetch it
            logger.log('[useAuth] Token valid, fetching user data...');
            const userData = await AuthService.getCurrentUser();
            logger.log('[useAuth] User data fetched:', {
              id: userData.id,
              email: userData.email,
              hasProfileImage: !!userData.profileImage,
              profileImage: userData.profileImage,
            });
            setUser(userData);
          }
        } else {
          logger.log(
            '[useAuth] Token invalid or missing, setting user to null'
          );
          setUser(null);
        }
      } catch (error) {
        logger.error('[useAuth] Error checking auth status:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (_email: string, _password: string) => {
    // Login is handled in LoginPage component
    // This function is kept for interface compatibility
    throw new Error('Login should be handled in LoginPage component');
  };

  const logout = async () => {
    try {
      await AuthService.logout();
    } catch (error) {
      logger.error('Error during logout:', error);
    } finally {
      setUser(null);
    }
  };

  const register = async (userData: Record<string, unknown>) => {
    setIsLoading(true);
    try {
      const data = await apiFetchJson<{
        data: {
          token?: string;
          tokens?: { accessToken: string; refreshToken: string };
          user: unknown;
        };
        message?: string;
      }>('/v1/auth/register', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify(userData),
      });

      // Store tokens if provided
      if (data.data?.tokens) {
        SecureTokenStorage.setTokens({
          accessToken: data.data.tokens.accessToken,
          refreshToken: data.data.tokens.refreshToken,
          rememberMe: false,
        });
      } else if (data.data?.token) {
        // Legacy format support
        SecureTokenStorage.setTokens({
          accessToken: data.data.token,
          refreshToken: '',
          rememberMe: false,
        });
      }

      // Clear cache and fetch fresh user data
      AuthService.clearCache();
      const user = await AuthService.getCurrentUser();
      setUser(user);
    } catch (error) {
      logger.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh user data from server
   * Forces a fresh fetch from API, bypassing cache
   */
  const refreshUser = async () => {
    try {
      const userData = await AuthService.getCurrentUser(true); // Force refresh
      setUser(userData);
      logger.log('[useAuth] User data refreshed successfully');
    } catch (error) {
      logger.error('[useAuth] Error refreshing user data:', error);
      // Don't clear user on refresh failure - keep existing data
      throw error;
    }
  };

  const value = {
    user,
    login,
    logout,
    register,
    setUser,
    refreshUser,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
